"use client";

import { Bars2Icon } from "@heroicons/react/24/outline";
import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import Link from "next/link";

import AccountMenu from "@/components/nav/AccountMenu";
import MobileNavMenu from "@/components/nav/MobileNavMenu";
import NavPanel from "@/components/nav/NavPanel";
import NavRail from "@/components/nav/NavRail";
import NavSearchDialog from "@/components/nav/NavSearchDialog";
import NavSearchPane from "@/components/nav/NavSearchPane";
import NavSearchRow from "@/components/nav/NavSearchRow";
import SearchShortcutsTip from "@/components/nav/SearchShortcutsTip";
import StudioBand from "@/components/nav/StudioBand";
import {
  SideNavSlotsProvider,
  useSideNavSlots,
} from "@/components/nav/SideNavSlots";
import TrainingModeNav from "@/components/nav/TrainingModeNav";
import { useStudioBranding } from "@/components/nav/useStudioBranding";
import { useStudioNavData } from "@/components/nav/useStudioNavData";
import { useWorkspaceSwitcher } from "@/components/nav/useWorkspaceSwitcher";
import { useNavShortcuts } from "@/components/nav/useNavShortcuts";
import { useNavSearch } from "@/components/nav/useNavSearch";
import { useNavTypeToSearch } from "@/components/nav/useNavTypeToSearch";
import { WorkspaceLogo } from "@/components/nav/WorkspaceSwitcher";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useApplePlatform } from "@/hooks/useApplePlatform";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  buildNavSections,
  collectNavShortcuts,
  createNavContext,
  hasVisibleNavItems,
  resolveActiveNavKey,
} from "@/lib/navigation/nav-items";
import { stripNavigationLocalePrefix } from "@/lib/navigation/is-nav-item-active";
import { openNavSearch } from "@/lib/navigation/nav-search-open";
import { isSimulatorRoute } from "@/lib/simulatorRoutes";
import { isTrainingModeRestrictedUser } from "@/lib/training-mode-users";

// The same key the old sidebar wrote, so a preference set before the move
// still holds. "true" means the panel is hidden.
const PANEL_HIDDEN_KEY = "salesably-sidebar-collapsed";
const OUTLINE_KEY = "outline";
// Where the icon rail shows (Tailwind's `md`). Search opens as a pane beside
// it; below this there is no rail, so search opens as a dialog instead.
const RAIL_SCREEN = "(min-width: 768px)";

const HEADER_BUTTON =
  "flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-stone-200/50 hover:text-gray-900";

/** The panel preference, read after mount so the server and client agree. */
function usePanelPreference() {
  const [open, setOpen] = useState(true);
  useEffect(() => {
    try {
      setOpen(window.localStorage.getItem(PANEL_HIDDEN_KEY) !== "true");
    } catch {
      // Storage blocked: the panel simply starts open.
    }
  }, []);
  const persist = (next) => {
    setOpen(next);
    try {
      window.localStorage.setItem(PANEL_HIDDEN_KEY, String(!next));
    } catch {
      // Same: nothing to do.
    }
  };
  return [open, persist];
}

/** The hash the address bar shows, kept current across in-page jumps. */
function useCurrentHash(pathname) {
  const [currentHash, setCurrentHash] = useState("");
  useEffect(() => {
    const read = () => setCurrentHash(window.location.hash.slice(1));
    read();
    window.addEventListener("hashchange", read);
    window.addEventListener("popstate", read);
    return () => {
      window.removeEventListener("hashchange", read);
      window.removeEventListener("popstate", read);
    };
  }, [pathname]);
  return [currentHash, setCurrentHash];
}

const StudioLogo = ({ src, name }) => (
  <WorkspaceLogo workspace={{ name, image: src }} compact />
);

/** Signed-out pages keep a plain top bar: the studio's mark and name. */
function SignedOutBar({ displayName, displayLogo }) {
  return (
    <header className="flex items-center gap-3 bg-surface-chrome px-4 py-2.5 md:px-6">
      <Link href="/" className="flex min-w-0 items-center gap-3">
        <StudioLogo src={displayLogo} name={displayName} />
        <span className="truncate text-lg font-bold text-gray-700">
          {displayName || "Salesably — Execution Excellence for Sales Teams"}
        </span>
      </Link>
    </header>
  );
}

function MobileHeader({ logoSrc, displayName, user, menuOpen, onOpenMenu }) {
  return (
    <header
      data-app-nav
      className="flex items-center justify-between gap-3 px-4 py-2 md:hidden print:hidden"
    >
      <Link href="/" className="flex min-w-0 items-center gap-2">
        <StudioLogo src={logoSrc} name={displayName} />
        <span className="truncate text-sm font-semibold text-gray-900">
          {displayName}
        </span>
      </Link>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Search"
          onClick={() => openNavSearch()}
          className={HEADER_BUTTON}
        >
          <Search className="h-5 w-5" />
        </button>
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={menuOpen}
          onClick={onOpenMenu}
          className={HEADER_BUTTON}
        >
          <Bars2Icon className="h-6 w-6" />
        </button>
        <AccountMenu user={user} side="bottom" />
      </div>
    </header>
  );
}

const menuView = (menu, child) =>
  menu && {
    key: menu.key,
    title: menu.menuLabel,
    subtitle: menu.subtitle,
    items: menu.items,
    crossLink: menu.crossLink,
    child,
  };

/**
 * The app's navigation: an icon rail with one button per menu beside a panel
 * listing the selected menu's links, and the page content on a white sheet to
 * their right. The studio's mark and search sit above both, as wide as the
 * nav; the account menu lives on the rail. Below `md` the rail becomes a
 * header with a drawer.
 *
 * What the menus contain is declared once in `lib/navigation/nav-manifest.js`
 * and resolved for this viewer by `lib/navigation/nav-items.js`; this file
 * only gathers the session, studio and module state those need and arranges
 * the pieces. Adding a nav link belongs in the manifest, not here.
 *
 * The panel shows the menu the route belongs to, or the page's own outline
 * when it registered one (SideNavSlots). A rail button, or the menu's name
 * in the outline's header, shows that menu instead; pressing the rail button
 * again hides the panel. The choice lasts until the route changes.
 */
function SideNavFrame({ children }) {
  const { data: session, update } = useSession();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const normalizedPathname = stripNavigationLocalePrefix(pathname || "");
  const isSimulator = isSimulatorRoute(normalizedPathname);

  const [currentHash, setCurrentHash] = useCurrentHash(pathname);
  const [menuOpen, setMenuOpen] = useState(false);
  const [panelOpen, setPanelOpen] = usePanelPreference();
  // In a live simulation the panel starts hidden and a peek is not remembered.
  const [peekOpen, setPeekOpen] = useState(false);
  const [pinnedKey, setPinnedKey] = useState(null);
  const searchButtonRef = useRef(null);
  const search = useNavSearch({ triggerRef: searchButtonRef });
  const { setOpen: setSearchOpen } = search;
  // The search row's element the pane renders its input into (NavSearchRow).
  const [searchInputSlot, setSearchInputSlot] = useState(null);
  const hasRail = useMediaQuery(RAIL_SCREEN);
  const apple = useApplePlatform();

  useEffect(() => {
    setMenuOpen(false);
    setPeekOpen(false);
    setPinnedKey(null);
    setSearchOpen(false);
  }, [pathname, setSearchOpen]);

  const { displayName, displayLogo } = useStudioBranding({
    session,
    searchParams,
    normalizedPathname,
  });
  const { enabledModules, isCommunityLeader, modules } =
    useStudioNavData(session);
  const switcherState = useWorkspaceSwitcher({ session, update });
  const { topAction, outline } = useSideNavSlots();

  const user = session?.user;
  const workspace = session?.workspace;
  const canSwitchStudios = Boolean(user?.superUser || user?.role === "admin");

  const menus = buildNavSections(
    createNavContext({
      user,
      workspace,
      enabledModules,
      modules,
      isCommunityLeader,
    }),
  ).filter((menu) => hasVisibleNavItems(menu.items));

  // Option/Alt + a letter goes straight to that page. The tour that teaches it
  // points at the first menu that has any, and quotes that menu's letters.
  const shortcuts =
    session && !isTrainingModeRestrictedUser(user)
      ? collectNavShortcuts(menus)
      : [];
  useNavShortcuts(shortcuts);
  const tipMenuRef = useRef(null);
  const tipMenuKey = shortcuts[0]?.menuKey;

  // Typing at the nav opens search with the shown menu's pages first.
  const activeMenuKey = resolveActiveNavKey(menus, normalizedPathname);
  const shownMenu =
    menus.find((menu) => menu.key === pinnedKey) ||
    menus.find((menu) => menu.key === activeMenuKey);
  const typeToSearchProps = useNavTypeToSearch(shownMenu?.menuLabel || "");

  if (isTrainingModeRestrictedUser(user)) {
    return (
      <>
        <TrainingModeNav
          session={session}
          normalizedPathname={normalizedPathname}
          displayLogo={displayLogo}
          displayName={displayName}
        />
        {children}
      </>
    );
  }

  if (!session) {
    return (
      <>
        <SignedOutBar displayName={displayName} displayLogo={displayLogo} />
        {children}
      </>
    );
  }

  // The outline stands in for the route's menu, so its header names that
  // menu as the way back; a rail button leads the same way. While the page
  // is open that menu names the outline as the way forward, so the trail
  // reads "Learn › Playbook" from either side.
  const activeMenu = menus.find((menu) => menu.key === activeMenuKey);
  const outlineView = outline && {
    key: OUTLINE_KEY,
    title: outline.title,
    items: outline.links,
    activeHrefs: outline.activeHrefs || [],
    parent: activeMenu && { key: activeMenu.key, label: activeMenu.menuLabel },
  };
  const pinnedMenu = menus.find((menu) => menu.key === pinnedKey);
  const view =
    (pinnedKey !== OUTLINE_KEY &&
      menuView(
        pinnedMenu,
        outline && pinnedMenu === activeMenu
          ? { key: OUTLINE_KEY, label: outline.title }
          : undefined,
      )) ||
    outlineView ||
    menuView(activeMenu);

  const panelVisible = Boolean(view) && (isSimulator ? peekOpen : panelOpen);
  // The search pane takes the panel's place while it is open; the panel's own
  // state is untouched, so closing the pane brings the panel back as it was.
  const panelShown = panelVisible && !search.open;
  // The band and the search row span the rail and whichever of the two is
  // showing; only with both hidden do they narrow to the rail.
  const navWide = panelShown || search.open;
  const showPanel = (next) =>
    isSimulator ? setPeekOpen(next) : setPanelOpen(next);
  const selectMenu = (key) => {
    search.close();
    if (panelVisible && view?.key === key) {
      setPinnedKey(null);
      showPanel(false);
      return;
    }
    setPinnedKey(key);
    showPanel(true);
  };
  const togglePanel = () => {
    if (search.open) {
      search.close();
      return;
    }
    if (panelVisible) setPinnedKey(null);
    showPanel(!panelVisible);
  };

  const studioName = workspace?.name || displayName;
  const logoSrc = workspace?.image || displayLogo;
  const drawerSections = [
    outline && {
      label: outline.title,
      subtitle: "On this page",
      items: outline.links,
      activeHrefs: outline.activeHrefs || [],
    },
    ...menus.map((menu) => ({
      label: menu.menuLabel,
      subtitle: menu.subtitle,
      items: menu.items,
    })),
  ].filter(Boolean);

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex min-h-0 grow flex-col bg-surface-chrome md:flex-row">
        <MobileHeader
          logoSrc={logoSrc}
          displayName={studioName}
          user={user}
          menuOpen={menuOpen}
          onOpenMenu={() => setMenuOpen(true)}
        />
        <MobileNavMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          pathname={pathname}
          currentHash={currentHash}
          onNavigateHash={setCurrentHash}
          sections={drawerSections}
        />

        <nav
          {...typeToSearchProps}
          data-app-nav
          aria-label="Main navigation"
          className="hidden md:sticky md:top-0 md:flex md:h-dvh md:shrink-0 md:flex-col md:self-start print:hidden"
        >
          <StudioBand
            displayName={studioName}
            displayLogo={logoSrc}
            switcher={
              canSwitchStudios && switcherState.availableWorkspaces.length > 0
                ? {
                    currentWorkspace: switcherState.switcherCurrentWorkspace,
                    workspaces: switcherState.availableWorkspaces,
                    value: switcherState.selectedWorkspaceId,
                    isSwitching: switcherState.isSwitchingWorkspace,
                    onSelect: switcherState.handleWorkspaceSwitch,
                  }
                : null
            }
            wide={navWide}
          />
          <NavSearchRow
            wide={navWide}
            open={search.open}
            buttonRef={searchButtonRef}
            inputSlotRef={setSearchInputSlot}
            onOpen={() => setSearchOpen(true)}
          />
          <div className="flex min-h-0 grow">
            <NavRail
              menus={menus}
              currentKey={activeMenuKey}
              openKey={panelShown ? view.key : null}
              onSelectMenu={selectMenu}
              isHome={normalizedPathname === "/"}
              user={user}
              panelOpen={panelShown}
              panelAvailable={Boolean(view) || search.open}
              onTogglePanel={togglePanel}
              menuButtonRef={tipMenuRef}
              tipMenuKey={tipMenuKey}
            />
            {view && (
              <NavPanel
                open={panelShown}
                view={view}
                topAction={view.key === activeMenuKey ? topAction : null}
                pathname={pathname}
                currentHash={currentHash}
                onNavigateHash={setCurrentHash}
                onSelectMenu={selectMenu}
              />
            )}
            <NavSearchPane
              search={search}
              open={search.open && hasRail}
              apple={apple}
              inputSlot={searchInputSlot}
            />
          </div>
        </nav>
        {/* `hasRail` is false until its effect runs, so the dialog is briefly
            mounted on a wide screen too — closed, because every way to open
            search is wired by an effect of the same commit. */}
        {!hasRail && <NavSearchDialog search={search} apple={apple} />}

        <SearchShortcutsTip
          searchButtonRef={searchButtonRef}
          menuRef={tipMenuRef}
          menuShortcuts={shortcuts.filter((s) => s.menuKey === tipMenuKey)}
          // Home only: other pages run spotlight tours of their own, and two at
          // once would stack.
          enabled={normalizedPathname === "/"}
        />

        {/* overflow-x-clip, not -hidden: hidden would make the sheet a scroll
            container and strand every `sticky` header inside it. */}
        <div className="flex min-h-0 min-w-0 grow flex-col overflow-x-clip bg-white md:mt-2.5 md:rounded-tl-3xl print:rounded-none">
          {children}
        </div>
      </div>
    </TooltipProvider>
  );
}

export default function SideNav({ children }) {
  return (
    <SideNavSlotsProvider>
      <SideNavFrame>{children}</SideNavFrame>
    </SideNavSlotsProvider>
  );
}
